import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './AppsPage.css';

// `superAdminOnly: true` keeps an app out of the catalogue for everyone else while it is still being
// finished. It is presentation only — the real gate is `requireSuperAdmin` on each of the app's routes in
// App.js, since hiding a card does not stop anyone typing the URL. Keep the two in step: when an app is
// opened up, drop the flag here AND relax its routes.
const APPS = [
    {
        name: 'Payment Porter',
        version: 'v1.0.0',
        description: 'Automatically processes each day\'s reservations, applying folio routing and profile assignments to virtual card and OTA bookings based on configurable rules.',
        category: 'Payment Processing',
        publisher: 'hconnect',
        path: '/apps/payment-porter',
        icon: (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="22.08" x2="12" y2="12" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
        ),
    },
    {
        name: 'Payment Migration Manager',
        version: 'v1.0.0',
        superAdminOnly: true,
        description: 'Apply payment actions to reservations across a date range. Filter by corporate profile, IATA, rate code or payment method.',
        category: 'Payment Processing',
        publisher: 'hconnect',
        path: '/apps/payment-migration-manager',
        icon: (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#16a34a" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17l10 5 10-5" stroke="#16a34a" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12l10 5 10-5" stroke="#16a34a" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
    },
    {
        name: 'Opera Cloud v Opera Cloud',
        version: 'v1.0.0',
        superAdminOnly: true,
        description: 'Compare configuration between two Oracle OPERA Cloud instances and repair differences — align settings from a source property onto a target.',
        category: 'Configuration',
        publisher: 'hconnect',
        path: '/apps/opera-cloud-compare',
        icon: (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M7.5 17a3.5 3.5 0 0 1-.5-6.96A4.5 4.5 0 0 1 15 9.5a3 3 0 0 1 .5 5.96" stroke="#7c3aed" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14.5 20a3 3 0 0 0 .35-5.98A3.75 3.75 0 0 0 8 12.6" stroke="#f59e0b" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M11 6.5h4m0 0-1.5-1.5M15 6.5 13.5 8" stroke="#7c3aed" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
    },
    {
        name: 'DVM Extractor',
        version: 'v1.0.0',
        superAdminOnly: true,
        description: 'Pull a property\'s Data Value Mappings out of OHIP and hand them back as Excel, CSV or JSON.',
        category: 'Configuration',
        publisher: 'hconnect',
        path: '/apps/dvm-extractor',
        icon: (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M4 4h7v7H4zM13 13h7v7h-7z" stroke="#db2777" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M11 7.5h4.5a1.5 1.5 0 0 1 1.5 1.5V13" stroke="#db2777" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 11l2 2 2-2" stroke="#db2777" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
    },
    {
        name: 'Dryft',
        version: 'v1.0.0',
        superAdminOnly: true,
        description: 'Daily OHIP property-config monitoring across many hotels. Watch selected configuration groups and get alerted by email or Teams when they drift.',
        category: 'Configuration',
        publisher: 'hconnect',
        path: '/apps/dryft',
        icon: (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M3 12h4l2-6 4 12 2-6h6" stroke="#0891b2" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
    },
];

// `embedded` drops the page chrome so the catalogue can sit inside another screen (the hotel's Products tab).
const AppsPage = ({ embedded = false }) => {
    const navigate = useNavigate();
    const { isSuperAdmin } = useAuth();

    const apps = APPS.filter(app => !app.superAdminOnly || isSuperAdmin);

    return (
        <div className={`apps-page${embedded ? ' apps-page-embedded' : ''}`}>
            {!embedded && (
                <div className="apps-header">
                    <h1 className="apps-title">Apps</h1>
                    <p className="apps-subtitle">Browse and manage applications for your hotels.</p>
                </div>
            )}
            <div className="apps-grid">
                {apps.map(app => (
                    <div
                        key={app.name}
                        className={`app-card${app.superAdminOnly ? ' sa-only' : ''}`}
                        onClick={() => navigate(app.path)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && navigate(app.path)}
                    >
                        <div className="app-card-top">
                            <div className="app-icon-wrap">
                                {app.icon}
                            </div>
                            <div className="app-card-title-block">
                                <div className="app-card-name">{app.name}</div>
                                <span className="app-version-badge">{app.version}</span>
                            </div>
                        </div>
                        <p className="app-card-desc">{app.description}</p>
                        <div className="app-card-footer">
                            <span className="app-category-pill">{app.category}</span>
                            <span className="app-publisher">by <span className="app-publisher-name">{app.publisher}</span></span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AppsPage;
